import { useCallback, useEffect, useRef } from 'react';
import {
  useEmployeeDimensionalFilter,
  type DimensionalFilterConfig,
  type UseEmployeeDimensionalFilterResult,
} from './useEmployeeDimensionalFilter';
import {
  useAttendanceSearchFilters,
  type SearchFilterConfig,
  type UseAttendanceSearchFiltersResult,
} from './useAttendanceSearchFilters';
import type { EmployeeFilteredStatus } from '@/api/domains/employee/types';

// The result emitted by onSearch — type is fixed, consumer decides what to do with it
export interface AttendanceFiltersResult {
  employeeIds: number[];       // [] means "all". Takes precedence over dimensional filters.
  dateFrom: string;            // yyyy-MM-dd
  dateTo: string;              // yyyy-MM-dd
  eventIds: number[];          // [] means "all events"
  markStatus?: number;         // 1 = Habilitado, 0 = Deshabilitado, undefined = all
  // Dimensional filters — resolve the employee universe when employeeIds is empty.
  companyIds: string[];
  areaIds: string[];
  positionIds: string[];
  costCenterIds: string[];
  locationIds: string[];
  contractModalityIds: string[];
  groupIds: string[];
  employeeStatus: EmployeeFilteredStatus; // 'vigente' | 'no_vigente' | 'todos', default 'vigente'
}

export interface UseAttendanceFiltersPanelOptions {
  dimensionalConfig: DimensionalFilterConfig;
  searchConfig: SearchFilterConfig;
  onSearch: (result: AttendanceFiltersResult) => void;
}

export interface UseAttendanceFiltersPanelResult {
  dimensionalFilter: UseEmployeeDimensionalFilterResult;
  searchFilters: UseAttendanceSearchFiltersResult;
  handleSearch: () => void;
  injectSearchErrors: UseAttendanceSearchFiltersResult['injectErrors'];
}

export function useAttendanceFiltersPanel(
  options: UseAttendanceFiltersPanelOptions,
): UseAttendanceFiltersPanelResult {
  const { dimensionalConfig, searchConfig, onSearch } = options;

  const dimensionalFilter = useEmployeeDimensionalFilter(dimensionalConfig);
  const searchFilters = useAttendanceSearchFilters(searchConfig);
  const { filters, clearErrors } = searchFilters;

  const handleSearch = useCallback(() => {
    clearErrors();

    const markStatus: number | undefined =
      filters.markStatus === '1' ? 1 :
        filters.markStatus === '0' ? 0 :
          undefined;

    onSearch({
      employeeIds: dimensionalFilter.selectedEmployeeIds,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      eventIds: filters.eventIds.map(Number).filter((n) => !isNaN(n)),
      markStatus,
      companyIds: dimensionalFilter.dimensionalFilters.companyIds,
      areaIds: dimensionalFilter.dimensionalFilters.areaIds,
      positionIds: dimensionalFilter.dimensionalFilters.positionIds,
      costCenterIds: dimensionalFilter.dimensionalFilters.costCenterIds,
      locationIds: dimensionalFilter.dimensionalFilters.locationIds,
      contractModalityIds: dimensionalFilter.dimensionalFilters.contractModalityIds,
      groupIds: dimensionalFilter.dimensionalFilters.groupIds,
      employeeStatus: dimensionalFilter.dimensionalFilters.status,
    });
  }, [
    filters,
    dimensionalFilter.selectedEmployeeIds,
    dimensionalFilter.dimensionalFilters,
    onSearch,
    clearErrors,
  ]);

  // Auto-search when the advanced (dimensional) panel is collapsed: changing the date range or a
  // quick filter (events/mark status) applies immediately, matching the plain filters the grid
  // already loaded with on mount. Expanded panel keeps the explicit "Buscar" button instead —
  // the effect is skipped there and on the very first render to avoid a redundant initial fetch.
  const isFirstRenderRef = useRef(true);
  const isExpanded = dimensionalFilter.isExpanded;

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    if (isExpanded) return;

    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.dateFrom, filters.dateTo, filters.eventIds, filters.markStatus, isExpanded]);

  return {
    dimensionalFilter,
    searchFilters,
    handleSearch,
    injectSearchErrors: searchFilters.injectErrors,
  };
}
